import { useLazyQuery } from "@apollo/client";
import {
  Anchor,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconExclamationMark, IconLock, IconMail } from "@tabler/icons-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSetAtom } from "jotai";
import {
  LoginUserQuery,
  LoginUserQueryVariables,
} from "../../../../gql/__generated__/graphql";
import { LOGIN_USER } from "../../../apis/lambda/GraphQL/User/queries";
import { userInfoAtom } from "../../../atoms";
import { decodeJWT, saveJWTCookie } from "../../../utils/jwtUtils";

interface LoginFormValues {
  email: string;
  password: string;
}

function LoginPage() {
  const navigate = useNavigate();
  const setUserInfo = useSetAtom(userInfoAtom);
  const [loginError, setLoginError] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) => (!/\S+@\S+\.\S+/.test(value) ? "Invalid email" : null),
      password: (value) =>
        value.length < 6 ? "Password must have at least 6 characters" : null,
    },
  });

  const [loginUser, { loading }] = useLazyQuery<
    LoginUserQuery,
    LoginUserQueryVariables
  >(LOGIN_USER, {
    onCompleted: (data) => {
      const jwt = data.loginUser;
      if (jwt) {
        saveJWTCookie(jwt);
        setUserInfo(decodeJWT(jwt));
        navigate("/");
      } else {
        setLoginError(
          "Login failed. Please check your credentials and try again."
        );
        notifications.show({
          title: "Login failed",
          message: "Please check your credentials and try again.",
          color: "red",
          icon: <IconExclamationMark size={16} />,
        });
      }
    },
    onError: () => {
      setLoginError(
        "Login failed. Please check your credentials and try again."
      );
      notifications.show({
        title: "Login failed",
        message: "An error occurred during login. Please try again.",
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
    },
  });

  function handleLogin(values: LoginFormValues) {
    setLoginError(null);
    loginUser({
      variables: {
        email: values.email,
        password: values.password,
      },
    });
  }

  return (
    <Container size="xs" py="xl">
      <Center mih="100vh">
        <Paper radius="md" p="xl" withBorder shadow="md" w="100%">
          <Title ta="center" order={2} mb="lg">
            Welcome back!
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb="xl">
            Log in to continue to Collabish
          </Text>

          <form onSubmit={form.onSubmit(handleLogin)}>
            <Stack>
              <TextInput
                label="Email"
                placeholder="Enter your email"
                leftSection={<IconMail size={16} />}
                {...form.getInputProps("email")}
              />
              <PasswordInput
                label="Password"
                placeholder="Enter your password"
                leftSection={<IconLock size={16} />}
                {...form.getInputProps("password")}
              />
              {loginError && (
                <Text c="red" size="sm" ta="center">
                  {loginError}
                </Text>
              )}
              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={loading}
                leftSection={loading ? <Loader size={16} /> : undefined}
              >
                Log In
              </Button>
            </Stack>
          </form>

          <Stack align="center" mt="xl" gap="xs">
            <Group justify="center" gap="xs">
              <Text size="sm" c="gray.6">
                Don't have an account?{" "}
                <Anchor component={Link} to="/signup" c="secondary" fw={600}>
                  Sign Up
                </Anchor>
              </Text>
            </Group>
            <Anchor
              component={Link}
              to="/login/forgot-password"
              size="sm"
              c="gray.6"
              td={"underline"}
            >
              Forgot your password?
            </Anchor>
          </Stack>
        </Paper>
      </Center>
    </Container>
  );
}

export default LoginPage;
